// The Argument Critique slide-over (spec 2026-06-07). Opens over whatever
// Build view is active via ?critique=true; two independent sections.
import { Scale, X } from "lucide-react";
import { useAnalyzeToolState } from "@/hooks/useAnalyzeToolState";
import { SoundnessSection } from "@/components/build/SoundnessSection";
import { PositionSection } from "@/components/build/PositionSection";
import type { StoredArgumentAnalysis } from "@/hooks/useClaims";

interface Props {
  conceptId: string;
  onClose: () => void;
  onShowClaim: (claimId: string) => void;
}

export function ArgumentCritiquePanel({ conceptId, onClose, onShowClaim }: Props) {
  const { critique, argumentAnalysis, positionAnalysis } =
    useAnalyzeToolState(conceptId);

  return (
    <div
      role="complementary"
      aria-label="Argument Critique"
      className="absolute inset-y-0 right-0 z-20 flex w-[480px] max-w-[85%] flex-col border-l border-border bg-background shadow-xl"
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Scale className="size-4 text-primary" />
        <h2 className="text-sm font-semibold">Argument Critique</h2>
        <button
          onClick={onClose}
          aria-label="Close critique panel"
          className="ml-auto rounded p-1 text-muted-foreground hover:bg-accent/50 hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        <p className="text-[11px] text-muted-foreground">
          Stress-test your claim structure, then locate it in the field.
        </p>
        <SoundnessSection
          conceptId={conceptId}
          stored={(argumentAnalysis ?? null) as StoredArgumentAnalysis | null}
          status={critique.soundness}
          onShowClaim={onShowClaim}
        />
        <PositionSection
          conceptId={conceptId}
          positionAnalysis={positionAnalysis ?? null}
          status={critique.position}
        />
      </div>
    </div>
  );
}
